import { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Plus } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { faqContent } from "@/i18n/faqContent";

const FAQ = () => {
  const { lang } = useLanguage();
  const reduce = useReducedMotion();
  const copy = faqContent[lang];
  const [open, setOpen] = useState(0);

  return (
    <section id="faq" data-testid="faq-section" className="scroll-mt-28 border-y border-line/80 bg-white py-20 md:py-28">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 md:px-8 lg:grid-cols-[0.78fr_1.22fr] lg:gap-20">
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
          className="lg:sticky lg:top-32 lg:self-start"
        >
          <span className="inline-flex items-center rounded-full border border-line bg-softwhite px-4 py-1.5 text-xs font-semibold text-sage-deep">
            {copy.eyebrow}
          </span>
          <h2 className="font-display mt-5 max-w-lg text-3xl font-bold text-charcoal sm:text-4xl lg:text-5xl">
            {copy.title}
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-graphite/65 md:text-lg">{copy.sub}</p>
        </motion.div>

        <div className="divide-y divide-line border-y border-line">
          {copy.items.map((item, index) => {
            const isOpen = open === index;
            const panelId = `faq-panel-${index}`;
            return (
              <div key={item.question} className="group">
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? -1 : index)}
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  data-testid={`faq-question-${index}`}
                  className="flex w-full cursor-pointer items-center justify-between gap-6 py-6 text-start text-base font-bold text-charcoal outline-none transition-colors duration-200 hover:text-sage-deep focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-4 md:py-7 md:text-lg"
                >
                  <span className="flex items-baseline gap-4">
                    <span className="text-xs font-semibold tabular-nums text-graphite/35" dir="ltr">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    {item.question}
                  </span>
                  <motion.span
                    aria-hidden="true"
                    animate={{ rotate: isOpen ? 45 : 0 }}
                    transition={{ duration: reduce ? 0 : 0.25 }}
                    className="flex size-9 shrink-0 items-center justify-center rounded-full border border-line bg-softwhite text-graphite"
                  >
                    <Plus className="size-4" />
                  </motion.span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      id={panelId}
                      initial={reduce ? false : { height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: reduce ? 0 : 0.3, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <p className="max-w-2xl px-11 pb-7 text-sm leading-7 text-graphite/70 md:text-base md:leading-8">
                        {item.answer}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FAQ;
